<template>
  <panel-layout
    id="customer-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="customer" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-2xl tracking-tighter font-bold leading-none/85">
            {{ customer.isNewRecord ? $t('customer.new') : customer.displayName }}
          </h2>
          <div class="mt-1">
            <p class="opacity-80 text-sm" v-html="$t('pages.customer.edit.index.created', { label: customer.createdLabel })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="customer" ref="form" class="h-full">
      <o-tabs
        nav-class="bg-gray-50 dark:bg-gray-900 h-11 border-b dark:border-gray-800"
        scroll
      >
        <o-tab :name="$t('general.information')" icon="attendees">
          <info v-model:customer="customer" @updateImageSuccess="handleChangedProfileImage" />
        </o-tab>
        <o-tab :name="$t('general.address')" icon="attendees">
          <address-details v-model:customer="customer" />
        </o-tab>
        <o-tab v-if="$features.get('custom-fields')" :name="$t('general.customFields')" icon="customFields">
          <custom-fields v-model:customer="customer" />
        </o-tab>
        <o-tab :name="$t('general.marketing')" icon="forms">
          <marketing v-model:customer="customer" />
        </o-tab>
        <o-tab v-if="$features.get('segments')" :name="$t('general.segments')" icon="communications">
          <segments v-model:customer="customer" />
        </o-tab>
      </o-tabs>
    </o-form>

    <template #footer>
      <div v-if="customer">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { CustomerPrivacyConsent } from '~/_api/models/customer'
// @ts-ignore
import Info from '@/components/customer/edit/information.vue'
// @ts-ignore
import AddressDetails from '@/components/customer/edit/address.vue'
// @ts-ignore
import CustomFields from '@/components/customer/edit/custom.vue'
// @ts-ignore
import Marketing from '@/components/customer/edit/marketing.vue'
// @ts-ignore
import Segments from '@/components/customer/edit/segments.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import {
  OTabs,
  OTab,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const emit = defineEmits<{
  (e: 'customer-updated', customer: any): void
}>()

const { $api, $bus, $auth, $notification, $i18n } = useNuxtApp()

const panel = usePanel()

const confirmDialog = ref<any>()
const form = ref<any>()

const customer = ref<any>(null)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(customer)

const handleChangedProfileImage = (c: any) => {
  $bus.emit.updated('Customer', c)
  emit('customer-updated', c)
}

const save = async (close = false) => {
  loading.value = true

  if (!await form.value?.validate()) {
    loading.value = false
    return false
  }

  const isNewRecord = customer.value.isNewRecord

  if (isNewRecord && $auth.info.location.customerConsentEnabled) {
    // CustomerPrivacyConsent is a string enum ('null' | 'true' | 'false').
    // Old code read the Enum wrapper's `.value` (returned the string 'true');
    // direct comparison with the enum constant is the current-era equivalent.
    if (customer.value.consentAccepted !== CustomerPrivacyConsent.accepted) {
      $notification.error({
        title: $i18n.t('pages.customer.edit.index.privacyConsentError'),
        text: $i18n.t('pages.customer.edit.index.privacyConsentErrorInfo'),
      })

      loading.value = false
      return false
    }
  }

  try {
    const result = (await $api.customer.save(customer.value)).data

    if (result) {
      customer.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.customer.edit.index.success'),
        text: $i18n.t('pages.customer.edit.index.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Customer', result)
      emit('customer-updated', result)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Swap this panel in-place to show the newly-created customer's edit view.
        panel.replace('customer/edit', { id: String(result.idCustomer) })
      }
    }
  } catch (e: any) {
    loading.value = false
    form.value?.setErrors(e?.errors)
    $notification.error({ errors: e?.errors })
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const init = async (id?: string) => {
  loading.value = true
  try {
    if (!id) {
      customer.value = (await $api.customer.defaults({} as any)).data
    } else {
      customer.value = (await $api.customer.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
